import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KiGeneratedBadge, StateChip } from '../../components/atoms.js';
import { type CatalogColumn, CatalogTable, columnWidth } from '../../components/catalog-table.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { ApiError } from '../../lib/api.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { type Simplification, simplificationsApi } from '../../lib/simplifications-api.js';
import { SimplificationNotePanel } from './simplification-note-panel.js';

// #52/#51: Simplifications war der letzte Katalog-Tab mit einem eigenen Bedienkonzept
// (nur Anlegen + Löschen) — jetzt auf CatalogTable wie Glossar/Testdaten. Neu erfassbar:
// Titel, SAP-Note, Beschreibung, Teilprojekt. Die Beschreibung ist als `description_html`
// gespeichert (kann Import-HTML enthalten), wird in V1 aber als reiner Text angezeigt UND
// bearbeitet — deshalb hier kein dangerouslySetInnerHTML: React escaped den Zelleninhalt beim
// Rendern automatisch, Tags erscheinen als sichtbarer Text statt ausgeführt zu werden.
//
// Teilprojekt (node_id) bekommt hier seinen einzigen Leser: eine Auswahl in der Zeile UND ein
// Filter über der Tabelle (KAT-3-Muster wie beim Testdaten-Tab — serverseitig über
// list(projectId, nodeId), Query-Key inkl. nodeFilter, kein Client-seitiges Sieben). Ohne den
// Filter wäre node_id nur ein weiteres Feld, das speichert und nichts bewirkt (genau die
// Fehlerklasse, derentwegen #52 existiert).

// KAT-1: SAP-Note-Nummer als Deep-Link zu rz10.de/<nummer> (leitet zur SAP-Note-Seite auf
// me.sap.com weiter). Nur rein numerische Werte werden verlinkt — Freitext bleibt reiner Text,
// damit ein unpassender Wert keinen ins Leere zeigenden Link erzeugt. Externer Tab + noopener.
function SapNoteCell({ note }: { note: string | null }) {
  if (!note) return <>—</>;
  const trimmed = note.trim();
  if (!/^\d+$/.test(trimmed)) return <>{note}</>;
  return (
    <a
      href={`https://rz10.de/${trimmed}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-accent hover:underline"
    >
      {note}
    </a>
  );
}

type Draft = {
  title: string;
  sap_note: string | null;
  description_html: string | null;
  node_id: string | null;
  is_exception: boolean;
};

// I4 (Review-Finding): Breiten getrennt exportiert, damit catalog-table.test.tsx
// computeTableMinWidth gegen die ECHTEN Breiten dieses Tabs pruefen kann. `description` (dritte
// Spalte) hat bewusst KEINE eigene Breite (undefined) — die unbepreiste Spalte.
// Spaltenbreiten justiert: SAP-Note, Abdeckung und Ausnahme tragen kurzen Inhalt (Nummer/Chip/
// Ja-Nein) und waren im Verhältnis zu breit; die eingesparte Fläche geht an die unbepreiste
// Beschreibungsspalte (dritte, `undefined`), die den meisten Text trägt. Prozentsumme 63% (< 100,
// vom Test abgesichert).
export const SIMPLIFICATIONS_COLUMN_WIDTHS: (string | undefined)[] = [
  '22%', // Titel
  '9%', // SAP-Note
  undefined, // Beschreibung — nimmt den Restplatz
  '15%', // Teilprojekt
  '9%', // Abdeckung
  '8%', // Ausnahme
];

// #33: abgeleiteter Abdeckungs-Zustand. Abgedeckt (>=1 verknuepfter Testfall) hat Vorrang, auch
// wenn is_exception zufaellig gesetzt ist; sonst Ausnahme (bewusst ohne Testfall), sonst offen.
// Ton-Zuordnung an StateChip gebunden (ok/warn/default), nicht selbst erfunden — sonst driftet sie.
function CoverageChip({ row, label }: { row: Simplification; label: (k: string) => string }) {
  if (row.linked_count > 0) return <StateChip label={label('covered')} tone="ok" />;
  if (row.is_exception) return <StateChip label={label('exception')} tone="warn" />;
  return <StateChip label={label('open')} tone="default" />;
}

export function SimplificationsTab({
  projectId,
  q,
}: {
  projectId: string;
  q?: string | undefined;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  const [nodeFilter, setNodeFilter] = useState('');
  // Task B3 (#53A): Einstieg zum Note-Panel — gegated wie jede andere Schreib-Aktion in diesem Tab.
  const [notePanelOpen, setNotePanelOpen] = useState(false);

  // Nur zur Ermittlung der Teilprojekt-Optionen (Auswahl + Filter) — es gibt keinen eigenen
  // Nodes-Endpunkt, catalogApi.tree(projectId).nodes ist die Quelle (siehe Task-Brief).
  const treeQuery = useQuery({
    queryKey: ['catalog-tree', projectId],
    queryFn: () => catalogApi.tree(projectId),
  });
  const nodes = treeQuery.data?.nodes ?? [];

  const rowsQuery = useQuery({
    queryKey: ['simplifications', projectId, nodeFilter],
    queryFn: () => simplificationsApi.list(projectId, nodeFilter || undefined),
  });

  // #78: Freitextfilter ueber die bereits geladenen Zeilen (kein neuer Endpunkt) — zusaetzlich
  // zum serverseitigen Teilprojekt-Filter oben (KAT-3). Der Teilprojekt-Name ist eine sichtbare
  // Spalte, deshalb zaehlt er mit. matchesQuery (Task 3) buendelt die Filterregel.
  const trimmedQuery = (q ?? '').trim();
  const searching = trimmedQuery.length > 0;
  const rows = rowsQuery.data ?? [];
  const filteredRows = useMemo(() => {
    if (!searching) return rows;
    return rows.filter((r) => {
      const nodeName = nodes.find((n) => n.node_id === r.node_id)?.name ?? '';
      return matchesQuery(trimmedQuery, r.title, r.sap_note, r.description_html, nodeName);
    });
  }, [rows, searching, trimmedQuery, nodes]);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['simplifications', projectId] });

  // errorToast: false auf allen drei Mutationen — CatalogTable zeigt Anlegen-/Bearbeiten- und
  // Loesch-Fehler bereits namentlich in der Zeile (Spec Kapitel 2), der globale Toast waere hier
  // die doppelte, generische zweite Meldung (#52-Review Finding 1).
  const createSimplification = useMutation({
    mutationFn: (d: Draft) => simplificationsApi.create(projectId, d),
    meta: { successToast: 'feedback.created', errorToast: false },
    onSuccess: invalidate,
  });
  const updateSimplification = useMutation({
    mutationFn: ({ id, d }: { id: string; d: Draft }) =>
      simplificationsApi.update(projectId, id, d),
    meta: { errorToast: false },
    onSuccess: invalidate,
  });
  const deleteSimplification = useMutation({
    mutationFn: (id: string) => simplificationsApi.remove(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: invalidate,
  });

  const nodeOptions = nodes.map((n) => ({ value: n.node_id, label: n.name }));

  const columns: CatalogColumn<Simplification, Draft>[] = [
    {
      key: 'title',
      header: t('simpl.titleField'),
      ...columnWidth(SIMPLIFICATIONS_COLUMN_WIDTHS[0]),
      // Task B3 (#53A): Abzeichen bei title/description GETRENNT je Feld (title_ai/description_ai)
      // — Bedingung wie bei den übrigen Katalog-Tabs (KiTranslatedBadge an *_ai-Flags).
      render: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 break-words">{r.title}</span>
          {r.title_ai && <KiGeneratedBadge />}
        </span>
      ),
      editor: { kind: 'text', get: (d) => d.title, set: (d, v) => ({ ...d, title: v }) },
    },
    {
      key: 'sap_note',
      header: t('simpl.sapNote'),
      ...columnWidth(SIMPLIFICATIONS_COLUMN_WIDTHS[1]),
      render: (r) => <SapNoteCell note={r.sap_note} />,
      editor: {
        kind: 'text',
        get: (d) => d.sap_note ?? '',
        set: (d, v) => ({ ...d, sap_note: v || null }),
      },
    },
    {
      key: 'description',
      header: t('catalog.fields.description'),
      // Bewusst als Text, nicht als HTML gerendert (siehe Kommentar oben): React escaped den
      // Wert automatisch, ein enthaltenes Tag erscheint als sichtbarer Text statt zu wirken.
      render: (r) => (
        <span className="flex min-w-0 items-start gap-2">
          <span className="min-w-0 break-words">{r.description_html ?? '—'}</span>
          {r.description_ai && <KiGeneratedBadge />}
        </span>
      ),
      editor: {
        kind: 'textarea',
        get: (d) => d.description_html ?? '',
        set: (d, v) => ({ ...d, description_html: v || null }),
      },
    },
    {
      key: 'node_id',
      header: t('catalog.fields.subproject'),
      ...columnWidth(SIMPLIFICATIONS_COLUMN_WIDTHS[3]),
      render: (r) => nodes.find((n) => n.node_id === r.node_id)?.name ?? '—',
      editor: {
        kind: 'select',
        get: (d) => d.node_id,
        set: (d, v) => ({ ...d, node_id: v }),
        options: nodeOptions,
        // Das Teilprojekt ist echt optional — anders als die Glossar-Quelle (#52-Review Finding
        // 1) gibt es hier einen Zustand "keinem Teilprojekt zugeordnet", der auch fachlich gilt.
        allowEmpty: true,
        emptyLabel: t('catalog.fields.subprojectNone'),
      },
    },
    {
      // #33-AC1: Abdeckungs-Indikator — nicht editierbar, abgeleitet aus linked_count + is_exception.
      key: 'coverage',
      header: t('simpl.coverageHeader'),
      ...columnWidth(SIMPLIFICATIONS_COLUMN_WIDTHS[4]),
      render: (r) => <CoverageChip row={r} label={(k) => t(`simpl.coverage.${k}`)} />,
    },
    {
      // #33-AC2: Ausnahme-Statusfeld — bewusst ohne Testfall, abgrenzbar von „noch offen".
      key: 'is_exception',
      header: t('simpl.exceptionField'),
      ...columnWidth(SIMPLIFICATIONS_COLUMN_WIDTHS[5]),
      render: (r) => (r.is_exception ? t('common.yes') : t('common.no')),
      editor: {
        kind: 'checkbox',
        get: (d) => d.is_exception,
        set: (d, v) => ({ ...d, is_exception: v }),
      },
    },
  ];

  return (
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-text-primary">{t('simpl.title')}</h2>
        <div className="flex items-center gap-2">
          {/* Task B3 (#53A): Einstieg zum Note-Panel — nur mit catalog.edit sichtbar, wie jede
              andere Schreib-Aktion in diesem Tab (Anlegen-Knopf ist ebenfalls canEdit-gated). */}
          {canEdit && (
            <button
              type="button"
              onClick={() => setNotePanelOpen(true)}
              className="rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent"
            >
              {/* Review M5: derselbe Text wie die Panel-Überschrift — ein Key genügt. */}✦{' '}
              {t('simpl.notePanel.title')}
            </button>
          )}
          <label className="text-xs text-text-muted" htmlFor="simplifications-node-filter">
            {t('catalog.fields.subproject')}
          </label>
          <select
            id="simplifications-node-filter"
            value={nodeFilter}
            onChange={(e) => setNodeFilter(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-body"
          >
            <option value="">{t('catalog.simplifications.allSubprojects')}</option>
            {nodes.map((n) => (
              <option key={n.node_id} value={n.node_id}>
                {n.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {notePanelOpen && (
        <SimplificationNotePanel projectId={projectId} onClose={() => setNotePanelOpen(false)} />
      )}

      {rowsQuery.isLoading ? (
        <div className="text-sm text-text-muted">{t('catalog.loading')}</div>
      ) : (
        <>
          {searching && filteredRows.length === 0 && <SearchEmptyNotice query={trimmedQuery} />}
          <CatalogTable
            columns={columns}
            rows={filteredRows}
            rowKey={(r) => r.simplification_id}
            toDraft={(r) => ({
              title: r.title,
              sap_note: r.sap_note,
              description_html: r.description_html,
              node_id: r.node_id,
              is_exception: r.is_exception,
            })}
            emptyDraft={{
              title: '',
              sap_note: null,
              description_html: null,
              node_id: null,
              is_exception: false,
            }}
            onCreate={(d) => createSimplification.mutateAsync(d)}
            onUpdate={(id, d) => updateSimplification.mutateAsync({ id, d })}
            onDelete={(id) => deleteSimplification.mutateAsync(id)}
            deleteLabel={(r) => r.title}
            mapError={(err) => {
              if (!(err instanceof ApiError) || err.status !== 409) return null;
              // Der Server liefert bei einem Unique-Verstoss die tatsächlich kollidierende
              // Simplification mit ({error, existing} als JSON-Body, siehe simplifications.ts).
              // Ein Testdouble oder ein konkurrierendes Delete zwischen Verstoss und Nachladen
              // (Server-Kommentar dort) liefert ggf. keinen JSON-Body oder existing: null —
              // dann bleibt die Meldung generisch statt einen Namen vorzutäuschen.
              let existingTitle: string | undefined;
              try {
                const parsed = JSON.parse(err.message) as { existing?: { title?: string } | null };
                existingTitle = parsed.existing?.title;
              } catch {
                // kein JSON-Body -> generische Meldung unten
              }
              return {
                columnKey: 'sap_note',
                message: existingTitle
                  ? t('errors.sap_note_exists_named', { title: existingTitle })
                  : t('errors.sap_note_exists'),
              };
            }}
            canEdit={canEdit}
            addLabel={t('simpl.new')}
          />
        </>
      )}
    </div>
  );
}
